import {
  Business as BusinessIcon,
  Check as CheckIcon,
  UnfoldMore as UnfoldMoreIcon,
  ChevronRight as ChevronRightIcon,
  Domain as DepartmentIcon,
  AccountBalance as DivisionIcon,
  Group as TeamIcon,
  Assignment as ProjectGroupIcon,
  Circle as DotIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Skeleton,
  Typography,
} from '@mui/material';
import { styled as muiStyled, useTheme } from '@mui/material/styles';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import Arranger from '../base/arranger';

import {
  DesignToken,
  type DesignTokenSet,
} from '~/client/components/base/core';
import { useDesignTokens } from '~/client/hooks/use-design-tokens';
import { useOrganizationBranding } from '~/client/hooks/use-organization-branding';
import * as GQL from '~/graphql/generated/client';
import { useSnackbarContext } from '~c/components/base/snackbar';
import { dashboardRouteUtil } from '~c/routes';

const SwitcherButton = muiStyled(Button, {
  shouldForwardProp: (prop) => prop !== 'tokens',
})<{ tokens?: DesignTokenSet }>(({ theme, tokens }) => {
  const t = tokens ?? DesignToken;
  return {
    border: 'none',
    minWidth: 24,
    minHeight: 24,
    color: `var(--org-primary-contrast, ${t.nav.itemTextOnDark})`,
    backgroundColor: 'transparent',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: t.nav.activeBgDark,
      color: `var(--org-primary-contrast, ${t.nav.itemTextOnDark})`,
      '& .MuiButton-endIcon': {
        color: `var(--org-primary-contrast, ${t.nav.itemTextOnDark})`,
        opacity: 1,
      },
    },
    '&.Mui-focusVisible': {
      backgroundColor: t.nav.activeBgDark,
      outline: 'none',
    },
    '& .MuiButton-endIcon': {
      color: `var(--org-primary-contrast, ${t.nav.itemTextOnDark})`,
      opacity: 0.9,
    },
  };
});

const RoleBadge = muiStyled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.text.secondary,
  padding: theme.spacing(0.25, 1),
  borderRadius: theme.spacing(0.5),
  fontSize: '0.75rem',
  fontWeight: 500,
  textTransform: 'uppercase',
  marginLeft: theme.spacing(1),
}));

const SmallAvatar = muiStyled(Avatar)(({ theme }) => ({
  width: 24,
  height: 24,
  backgroundColor: theme.palette.primary.main,
}));

const MediumAvatar = muiStyled(Avatar)(({ theme }) => ({
  width: 32,
  height: 32,
  backgroundColor: theme.palette.primary.main,
}));

const CollapsedIconButton = muiStyled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'tokens',
})<{ tokens?: DesignTokenSet }>(({ tokens }) => {
  const t = tokens ?? DesignToken;
  return {
    width: 40,
    height: 40,
    color: `var(--org-primary-contrast, ${t.nav.itemTextOnDark})`,
    backgroundColor: t.nav.activeBgDark,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
      color: `var(--org-primary-contrast, ${t.nav.itemTextOnDark})`,
    },
  };
});

const StyledBusinessIcon = muiStyled(BusinessIcon)(() => ({
  fontSize: 24,
}));

const SmallBusinessIcon = muiStyled(BusinessIcon)(() => ({
  fontSize: 14,
}));

// Hierarchical Tree Styled Components - matching navbar branding colors
const StyledPopover = muiStyled(Popover, {
  shouldForwardProp: (prop) => prop !== 'tokens',
})<{ tokens?: DesignTokenSet }>(({ tokens }) => {
  const t = tokens ?? DesignToken;
  return {
    '& .MuiPopover-paper': {
      backgroundColor: `var(--org-background-color, ${t.nav.sidebarBg})`,
      minWidth: 280,
      maxWidth: 360,
      marginTop: 4,
      border: '1px solid rgba(255, 255, 255, 0.12)',
      boxShadow: t.shadowMedium,
      borderRadius: `${t.radiusSmall}px`,
    },
  };
});

const TreeContainer = muiStyled(Box)(() => ({
  maxHeight: 400,
  overflow: 'auto',
  padding: '4px 0',
}));

const OrganizationName = muiStyled(Typography, {
  shouldForwardProp: (prop) => prop !== 'tokens',
})<{ tokens?: DesignTokenSet }>(({ tokens }) => {
  const t = tokens ?? DesignToken;
  return {
    fontSize: '15px',
    fontWeight: 400,
    lineHeight: 1.4,
    fontStyle: 'normal',
    color: `var(--org-primary-contrast, ${t.nav.itemTextOnDark})`,
  };
});

const OrganizationTreeItem = muiStyled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'tokens',
})<{ selected?: boolean; tokens?: DesignTokenSet }>(({
  theme,
  selected,
  tokens,
}) => {
  const t = tokens ?? DesignToken;
  return {
    padding: theme.spacing(1.25, 1.5),
    margin: theme.spacing(0, 1),
    marginBottom: theme.spacing(0.25),
    borderRadius: `${t.radiusSmall}px`,
    height: '44px',
    transition: 'background-color 0.2s ease, color 0.2s ease',
    backgroundColor: selected
      ? `var(--org-primary-color, ${t.nav.sidebarBg})`
      : 'transparent',
    '&:hover': {
      backgroundColor: selected
        ? `var(--org-primary-color, ${t.nav.sidebarBg})`
        : t.nav.activeBgDark,
    },
  };
});

const UnitTreeItem = muiStyled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'tokens',
})<{
  level: number;
  selected?: boolean;
  tokens?: DesignTokenSet;
}>(({ theme, level, selected, tokens }) => {
  const t = tokens ?? DesignToken;
  return {
    padding: theme.spacing(1.25, 1.5),
    paddingLeft: theme.spacing(3 + level * 2),
    margin: theme.spacing(0, 1),
    marginBottom: theme.spacing(0.25),
    borderRadius: `${t.radiusSmall}px`,
    height: '44px',
    transition: 'background-color 0.2s ease, color 0.2s ease',
    position: 'relative',
    backgroundColor: selected
      ? `var(--org-primary-color, ${t.nav.sidebarBg})`
      : 'transparent',
    '&:hover': {
      backgroundColor: selected
        ? `var(--org-primary-color, ${t.nav.sidebarBg})`
        : t.nav.activeBgDark,
    },
  };
});

const OrganizationAvatar = muiStyled(Avatar, {
  shouldForwardProp: (prop) => prop !== 'tokens',
})<{ tokens?: DesignTokenSet }>(({ tokens }) => {
  const t = tokens ?? DesignToken;
  return {
    width: 28,
    height: 28,
    backgroundColor: `var(--org-primary-color, ${t.nav.sidebarBgElevated})`,
    color: `var(--org-primary-contrast, ${t.nav.itemTextOnDark})`,
    fontSize: '12px',
    fontWeight: 600,
  };
});

const UnitIconWrapper = muiStyled(Box, {
  shouldForwardProp: (prop) => prop !== 'tokens',
})<{ tokens?: DesignTokenSet }>(({ theme, tokens }) => {
  const t = tokens ?? DesignToken;
  return {
    width: 20,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: `var(--org-secondary-color, ${t.nav.itemTextOnDarkMuted})`,
    opacity: 0.8,
    marginRight: theme.spacing(1),
  };
});

const UnitListItemText = muiStyled(ListItemText, {
  shouldForwardProp: (prop) => prop !== 'tokens',
})<{ selected?: boolean; tokens?: DesignTokenSet }>(({ selected, tokens }) => {
  const t = tokens ?? DesignToken;
  return {
    '& .MuiListItemText-primary': {
      color: selected
        ? `var(--org-primary-contrast, ${t.nav.itemTextOnDark})`
        : `var(--org-secondary-color, ${t.nav.itemTextOnDarkMuted})`,
      fontSize: '14px',
      fontWeight: selected ? 500 : 400,
      lineHeight: 1.3,
      fontStyle: 'normal',
    },
    '& .MuiListItemText-secondary': {
      color: `color-mix(in srgb, var(--org-secondary-color, ${t.nav.itemTextOnDarkMuted}) 80%, transparent)`,
      fontSize: '11px',
      lineHeight: 1.2,
      fontStyle: 'normal',
      textTransform: 'capitalize',
    },
  };
});

const SelectedCheckIcon = muiStyled(CheckIcon, {
  shouldForwardProp: (prop) => prop !== 'tokens',
})<{ tokens?: DesignTokenSet }>(({ tokens }) => {
  const t = tokens ?? DesignToken;
  return {
    fontSize: 16,
    marginLeft: 8,
    color: `var(--org-primary-contrast, ${t.nav.itemTextOnDark})`,
  };
});

const LoadingContainer = muiStyled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
}));

const LoadingSkeleton = muiStyled(Skeleton, {
  shouldForwardProp: (prop) => prop !== 'tokens',
})<{ tokens?: DesignTokenSet }>(({ tokens }) => {
  const t = tokens ?? DesignToken;
  return {
    marginBottom: 4,
    borderRadius: t.radiusSmall / 2,
    backgroundColor: `color-mix(in srgb, var(--org-secondary-color, ${t.nav.itemTextOnDarkMuted}) 15%, transparent)`,
  };
});

const ExpandIconWrapper = muiStyled(Box, {
  shouldForwardProp: (prop) => prop !== 'tokens',
})<{ expanded: boolean; tokens?: DesignTokenSet }>(({ expanded, tokens }) => {
  const t = tokens ?? DesignToken;
  return {
    width: 16,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: `color-mix(in srgb, var(--org-secondary-color, ${t.nav.itemTextOnDarkMuted}) 60%, transparent)`,
    transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
    transition: 'transform 0.2s ease-in-out',
    marginRight: 4,
  };
});

const PopoverHeader = muiStyled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid rgba(255, 255, 255, 0.15)`,
}));

// Utility Functions
const getUnitTypeIcon = (unitType: GQL.OrganizationUnitType) => {
  switch (unitType) {
    case GQL.OrganizationUnitType.Department:
      return DepartmentIcon;
    case GQL.OrganizationUnitType.Division:
      return DivisionIcon;
    case GQL.OrganizationUnitType.Team:
      return TeamIcon;
    case GQL.OrganizationUnitType.ProjectGroup:
      return ProjectGroupIcon;
    default:
      return DotIcon;
  }
};

const getUnitTypeLabel = (unitType: GQL.OrganizationUnitType): string => {
  switch (unitType) {
    case GQL.OrganizationUnitType.Department:
      return 'Department';
    case GQL.OrganizationUnitType.Division:
      return 'Division';
    case GQL.OrganizationUnitType.Team:
      return 'Team';
    case GQL.OrganizationUnitType.ProjectGroup:
      return 'Project Group';
    default:
      return 'Unit';
  }
};

const getOrganizationInitials = (name: string): string => {
  return name
    .split(' ')
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Organization Unit Node Component
interface OrganizationUnitNodeProps {
  node: GQL.OrganizationUnitTreeNode;
  level: number;
  selectedUnitId?: string | null;
  onUnitSelect: (unitId: string | null) => void;
  expandedNodes: Set<string>;
  onToggleExpand: (nodeId: string) => void;
  tokens?: DesignTokenSet;
}

const OrganizationUnitNode: React.FC<OrganizationUnitNodeProps> = React.memo(
  ({
    node,
    level,
    selectedUnitId,
    onUnitSelect,
    expandedNodes,
    onToggleExpand,
    tokens,
  }) => {
    const IconComponent = getUnitTypeIcon(node.unitType);
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const isSelected = selectedUnitId === node.id;

    const handleNodeClick = useCallback(() => {
      onUnitSelect(node.id);
    }, [node.id, onUnitSelect]);

    const handleExpandClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onToggleExpand(node.id);
      },
      [node.id, onToggleExpand]
    );

    return (
      <>
        <ListItem disablePadding>
          <UnitTreeItem
            level={level}
            selected={isSelected}
            onClick={handleNodeClick}
            tokens={tokens}
          >
            {hasChildren ? (
              <ExpandIconWrapper
                expanded={isExpanded}
                onClick={handleExpandClick}
                tokens={tokens}
              >
                <ChevronRightIcon sx={{ fontSize: 16 }} />
              </ExpandIconWrapper>
            ) : (
              <Box sx={{ width: 20, height: 16 }} />
            )}

            <UnitIconWrapper tokens={tokens}>
              <IconComponent sx={{ fontSize: 16, marginRight: 2 }} />
            </UnitIconWrapper>

            <UnitListItemText
              primary={
                <OrganizationName tokens={tokens}>{node.name}</OrganizationName>
              }
              secondary={getUnitTypeLabel(node.unitType)}
              selected={isSelected}
              tokens={tokens}
            />

            {isSelected && <SelectedCheckIcon tokens={tokens} />}
          </UnitTreeItem>
        </ListItem>

        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List disablePadding>
              {node.children!.map((child) => (
                <OrganizationUnitNode
                  key={child.id}
                  node={child}
                  level={level + 1}
                  selectedUnitId={selectedUnitId}
                  onUnitSelect={onUnitSelect}
                  expandedNodes={expandedNodes}
                  onToggleExpand={onToggleExpand}
                  tokens={tokens}
                />
              ))}
            </List>
          </Collapse>
        )}
      </>
    );
  }
);
OrganizationUnitNode.displayName = 'OrganizationUnitNode';

// Organization Unit Tree Component
interface OrganizationUnitTreeInPopoverProps {
  organizationId: string;
  selectedUnitId?: string | null;
  onUnitSelect: (unitId: string | null) => void;
  tokens?: DesignTokenSet;
}

const OrganizationUnitTreeInPopover: React.FC<OrganizationUnitTreeInPopoverProps> =
  React.memo(({ organizationId, selectedUnitId, onUnitSelect, tokens }) => {
    const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

    const { data, loading, error } = GQL.useOrganizationUnitTreeQuery({
      variables: { organizationId },
      skip: !organizationId,
      fetchPolicy: 'cache-first',
    });

    const handleToggleExpand = useCallback((nodeId: string) => {
      setExpandedNodes((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(nodeId)) {
          newSet.delete(nodeId);
        } else {
          newSet.add(nodeId);
        }
        return newSet;
      });
    }, []);

    // Auto-expand path to selected unit
    const findAndExpandPath = useCallback(
      (
        node: GQL.OrganizationUnitTreeNode,
        targetId: string,
        path: string[] = []
      ): boolean => {
        if (node.id === targetId) {
          setExpandedNodes((prev) => new Set([...prev, ...path]));
          return true;
        }

        if (node.children) {
          for (const child of node.children) {
            if (findAndExpandPath(child, targetId, [...path, node.id])) {
              return true;
            }
          }
        }

        return false;
      },
      []
    );

    useEffect(() => {
      if (selectedUnitId && data?.organizationUnitTree) {
        findAndExpandPath(data.organizationUnitTree, selectedUnitId);
      }
    }, [selectedUnitId, data?.organizationUnitTree, findAndExpandPath]);

    if (loading) {
      return (
        <LoadingContainer>
          {[...Array(3)].map((_, index) => (
            <LoadingSkeleton
              key={index}
              variant="rectangular"
              height={24}
              tokens={tokens}
            />
          ))}
        </LoadingContainer>
      );
    }

    if (error || !data?.organizationUnitTree) {
      return null;
    }

    // Only show if there are children (organizational units)
    if (
      !data.organizationUnitTree.children ||
      data.organizationUnitTree.children.length === 0
    ) {
      return null;
    }

    return (
      <List
        disablePadding
        sx={{
          pl: 2,
          ml: 2,
          borderLeft: `1px solid rgba(255, 255, 255, 0.15)`,
          mb: 0.5,
        }}
      >
        {data.organizationUnitTree.children.map((child) => (
          <OrganizationUnitNode
            key={child.id}
            node={child}
            level={0}
            selectedUnitId={selectedUnitId}
            onUnitSelect={onUnitSelect}
            expandedNodes={expandedNodes}
            onToggleExpand={handleToggleExpand}
            tokens={tokens}
          />
        ))}
      </List>
    );
  });
OrganizationUnitTreeInPopover.displayName = 'OrganizationUnitTreeInPopover';

interface OrganizationContextSwitcherProps {
  variant?: 'button' | 'compact';
  showRoleLabels?: boolean;
  isCollapsed?: boolean;
  data: GQL.UserOrganizationContextsQuery | undefined;
  selectedUnitId?: string | null;
  onUnitSelect?: (unitId: string | null) => void;
}

export const OrganizationContextSwitcher: React.FC<
  OrganizationContextSwitcherProps
> = ({
  variant = 'button',
  showRoleLabels = true,
  isCollapsed = false,
  data,
  selectedUnitId,
  onUnitSelect,
}) => {
  const theme = useTheme();
  const t = useDesignTokens();
  const isDark = theme.palette.mode === 'dark';
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set());
  const Snackbar = useSnackbarContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { navigateTo: dashboarNaviagateTo } = dashboardRouteUtil.useHelper();
  const { setCurrentOrgId } = useOrganizationBranding();

  const [switchContext, { loading: switchLoading }] =
    GQL.useSwitchOrganizationContextMutation({
      onCompleted: (data_) => {
        if (data_.switchOrganizationContext.success) {
          dashboarNaviagateTo('COMPLIANCE');
          window.location.reload();
        } else {
          Snackbar.open(
            data_.switchOrganizationContext.message ??
              'Failed to switch organization',
            {
              showOk: true,
            }
          );
        }
      },
      onError: (error_) => {
        Snackbar.open(error_.message, {
          showOk: true,
        });
      },
    });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectOrganization = async (organizationId: string) => {
    if (organizationId === currentOrganizationId) {
      // Clicking current org clears unit selection (selects parent org)
      if (selectedUnitId && onUnitSelect) {
        onUnitSelect(null);
        handleClose();
        if (location.pathname !== '/dashboard/compliance') {
          navigate('/dashboard/compliance');
        }
      }
      return;
    }

    // Switching to a different organization
    try {
      // Update branding provider and localStorage so sidebar reflects new org (e.g. before reload)
      setCurrentOrgId(organizationId);

      await switchContext({
        variables: {
          input: { organizationId },
        },
      });
    } catch (err) {
      // Revert branding provider and localStorage on error
      if (currentOrganizationId) {
        setCurrentOrgId(currentOrganizationId);
      } else {
        setCurrentOrgId(null);
      }
      // Error handled in onError callback
    }
  };

  const handleToggleOrgExpand = useCallback((orgId: string) => {
    setExpandedOrgs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orgId)) {
        newSet.delete(orgId);
      } else {
        newSet.add(orgId);
      }
      return newSet;
    });
  }, []);

  const handleUnitSelect = useCallback(
    (unitId: string | null) => {
      if (onUnitSelect) {
        onUnitSelect(unitId);
        handleClose();
        if (location.pathname !== '/dashboard/compliance') {
          navigate('/dashboard/compliance');
        }
      }
    },
    [onUnitSelect, location.pathname, navigate]
  );

  const memberships = data?.userOrganizationContexts ?? [];
  const currentUser = data?.currentUserContext;
  const currentOrganizationId = currentUser?.defaultOrganizationId;

  // Find current organization details
  const currentOrganization = memberships.find(
    (membership) => membership.organization?._id === currentOrganizationId
  );

  // Fetch organization unit tree to find selected unit name
  const { data: unitTreeData } = GQL.useOrganizationUnitTreeQuery({
    variables: { organizationId: currentOrganizationId ?? '' },
    skip: !currentOrganizationId || !selectedUnitId,
    fetchPolicy: 'cache-first',
  });

  // Helper function to find unit name in tree
  const findUnitName = useCallback(
    (
      node: GQL.OrganizationUnitTreeNode | null | undefined,
      unitId: string
    ): string | null => {
      if (!node) {
        return null;
      }
      if (node.id === unitId) {
        return node.name;
      }
      if (node.children) {
        for (const child of node.children) {
          const found = findUnitName(child, unitId);
          if (found) {
            return found;
          }
        }
      }
      return null;
    },
    []
  );

  // Get the display name - either selected unit name or organization name
  const displayName = React.useMemo(() => {
    if (selectedUnitId && unitTreeData?.organizationUnitTree) {
      const unitName = findUnitName(
        unitTreeData.organizationUnitTree,
        selectedUnitId
      );
      if (unitName) {
        return unitName;
      }
    }
    return currentOrganization?.organization?.name ?? 'Select Organization';
  }, [
    selectedUnitId,
    unitTreeData?.organizationUnitTree,
    findUnitName,
    currentOrganization?.organization?.name,
  ]);

  // Auto-expand current organization on mount
  useEffect(() => {
    if (currentOrganizationId) {
      setExpandedOrgs((prev) => new Set([...prev, currentOrganizationId]));
    }
  }, [currentOrganizationId]);

  if (memberships.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No organizations available
      </Typography>
    );
  }

  if (memberships.length === 1 && variant === 'compact') {
    // If user only has one organization, show it without dropdown
    const org = memberships[0];
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <SmallAvatar>
          <SmallBusinessIcon />
        </SmallAvatar>
        <Typography variant="body2" fontWeight={500}>
          {org.organization?.name}
        </Typography>
        {showRoleLabels && (
          <RoleBadge>{org.role === 'ORG_ADMIN' ? 'Admin' : 'Member'}</RoleBadge>
        )}
      </Box>
    );
  }

  return (
    <Box>
      {isCollapsed ? (
        <CollapsedIconButton
          tokens={t}
          onClick={handleClick}
          disabled={switchLoading}
        >
          <MediumAvatar>
            <StyledBusinessIcon />
          </MediumAvatar>
        </CollapsedIconButton>
      ) : (
        <Arranger direction="row" alignItems="center" justify="space-between">
          {' '}
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            sx={{ minWidth: 0, flex: 1 }}
          >
            <StyledBusinessIcon
              sx={{
                fontSize: 24,
                color: `var(--org-primary-contrast, ${t.nav.itemTextOnDark})`,
                marginLeft: 'auto',
              }}
            />
            <OrganizationName tokens={t}>{displayName}</OrganizationName>
          </Box>
          <SwitcherButton
            tokens={t}
            onClick={handleClick}
            endIcon={
              <UnfoldMoreIcon
                sx={{
                  fontSize: 24,
                  color: `var(--org-primary-contrast, ${t.nav.itemTextOnDark})`,
                }}
              />
            }
            disabled={switchLoading}
          />
        </Arranger>
      )}

      <StyledPopover
        tokens={t}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            // Dark mode: always use nav tokens (ignore --org-* so popup matches dark sidebar, not teal)
            // Light mode: use org branding var or fallback
            ...(isDark
              ? {
                  backgroundColor: `${t.nav.sidebarBg} !important`,
                  background: `${t.nav.sidebarBg} !important`,
                }
              : {
                  backgroundColor: `var(--org-background-color, ${t.nav.sidebarBg}) !important`,
                  background: `var(--org-background-color, ${t.nav.sidebarBg}) !important`,
                }),
            color: `${t.nav.itemTextOnDark} !important`,
            minWidth: 280,
            maxWidth: 360,
            marginTop: 4,
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: t.radiusSmall,
            boxShadow: t.shadowMedium,
            // Unselected rows: readable text on dark/teal paper
            '& .MuiListItemButton-root:not(.Mui-selected)': {
              color: t.nav.itemTextOnDark,
              backgroundColor: 'transparent',
              '& .MuiListItemText-primary, & .MuiListItemIcon-root': {
                color: 'inherit',
              },
            },
            '& .MuiListItemButton-root:not(.Mui-selected):hover': {
              backgroundColor: t.nav.activeBgDark,
              color: t.nav.itemTextOnDark,
            },
            // Selected row (dark: use nav tokens; light: use org var)
            '& .MuiListItemButton-root.Mui-selected': {
              backgroundColor: `${
                isDark
                  ? t.nav.sidebarBg
                  : `var(--org-primary-color, ${t.nav.sidebarBg})`
              } !important`,
              color: `var(--org-primary-contrast, ${t.nav.itemTextOnDark}) !important`,
              '& .MuiListItemText-primary, & .MuiListItemIcon-root, & .MuiSvgIcon-root':
                {
                  color: `var(--org-primary-contrast, ${t.nav.itemTextOnDark}) !important`,
                },
            },
            '& .MuiListItemButton-root.Mui-selected:hover': {
              backgroundColor: `${
                isDark
                  ? t.nav.sidebarBg
                  : `var(--org-primary-color, ${t.nav.sidebarBg})`
              } !important`,
            },
          },
        }}
      >
        <TreeContainer>
          <List sx={{ py: 0 }}>
            {memberships.map((membership) => {
              const orgId = membership.organization?._id;
              const isSelected = orgId === currentOrganizationId;
              const isExpanded = orgId ? expandedOrgs.has(orgId) : false;
              const isLoading =
                switchLoading && orgId !== currentOrganizationId;
              const orgName = membership.organization?.name ?? '';

              return (
                <React.Fragment key={membership._id}>
                  {/* Organization Item */}
                  <ListItem disablePadding>
                    <OrganizationTreeItem
                      tokens={t}
                      selected={isSelected && !selectedUnitId}
                      onClick={() => orgId && handleSelectOrganization(orgId)}
                      disabled={isLoading || !orgId}
                    >
                      {/* Expand/Collapse Toggle - only show for selected org */}
                      {/* Placeholder for unselected to maintain consistent avatar alignment */}
                      {isSelected ? (
                        <ExpandIconWrapper
                          tokens={t}
                          expanded={isExpanded}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (orgId) {
                              handleToggleOrgExpand(orgId);
                            }
                          }}
                          sx={{ cursor: 'pointer', marginRight: 2 }}
                        >
                          <ChevronRightIcon sx={{ fontSize: 16 }} />
                        </ExpandIconWrapper>
                      ) : (
                        <Box sx={{ width: 20, marginRight: 1 }} />
                      )}

                      <ListItemIcon sx={{ minWidth: isSelected ? 48 : 48 }}>
                        {isLoading ? (
                          <CircularProgress
                            size={24}
                            sx={{
                              color: `var(--org-secondary-color, ${t.nav.itemTextOnDarkMuted})`,
                            }}
                          />
                        ) : (
                          <OrganizationAvatar
                            tokens={t}
                            sx={{ width: 32, height: 32 }}
                          >
                            {getOrganizationInitials(orgName)}
                          </OrganizationAvatar>
                        )}
                      </ListItemIcon>

                      <ListItemText
                        primary={
                          <OrganizationName tokens={t}>
                            {orgName}
                          </OrganizationName>
                        }
                        secondary={
                          selectedUnitId ? (
                            <Typography
                              variant="caption"
                              sx={{
                                color: `color-mix(in srgb, var(--org-secondary-color, ${t.nav.itemTextOnDarkMuted}) 70%, transparent)`,
                                fontSize: '12px',
                                lineHeight: 1.2,
                                fontStyle: 'normal',
                              }}
                            >
                              {membership.organization?.identifier}
                            </Typography>
                          ) : null
                        }
                      />

                      {isSelected && (
                        <CheckIcon
                          sx={{
                            fontSize: 20,
                            color: `var(--org-secondary-color, ${t.nav.itemTextOnDarkMuted})`,
                            ml: 1,
                          }}
                        />
                      )}
                    </OrganizationTreeItem>
                  </ListItem>

                  {/* Organization Unit Tree (only for selected org) */}
                  {isSelected && orgId && (
                    <Collapse in={isExpanded} timeout={300} unmountOnExit>
                      <OrganizationUnitTreeInPopover
                        organizationId={orgId}
                        selectedUnitId={selectedUnitId}
                        onUnitSelect={handleUnitSelect}
                        tokens={t}
                      />
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </List>
        </TreeContainer>
      </StyledPopover>
    </Box>
  );
};
